import type { ReactNode } from 'react';
import { useAuth } from '@/providers';
import { useEffect, useState } from 'react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Users, ArrowLeft, Loader2 } from 'lucide-react';

interface BillingGuardProps {
    children: ReactNode;
}

/**
 * BillingGuard component protects billing routes from team members who aren't owners
 * Only allows access to:
 * - Individual users (not part of any team)
 * - Team owners
 */
export function BillingGuard({ children }: BillingGuardProps) {
    const { getUserPrimaryTeam, isTeamOwner, teams, isLoading: authLoading, refreshTeams } = useAuth();
    const [loading, setLoading] = useState(true);
    const [userTeam, setUserTeam] = useState<any>(null);
    const [isOwner, setIsOwner] = useState(false);

    useEffect(() => {
        checkTeamAccess();
    }, [teams, authLoading]);

    // Ensure we have the freshest teams right after auth finishes loading (e.g., after accepting an invite)
    useEffect(() => {
        if (authLoading) return;
        (async () => {
            try {
                await refreshTeams();
            } catch (e) {
                // best effort; ignore
            }
        })();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [authLoading]);

    const checkTeamAccess = () => {
        try {
            // Don't check until auth is fully loaded
            if (authLoading) {
                return;
            }

            const teamData = getUserPrimaryTeam();
            setUserTeam(teamData);

            if (teamData) {
                const ownerStatus = isTeamOwner(teamData.team.id);
                setIsOwner(ownerStatus);
            } else {
                setIsOwner(false);
            }
        } catch (error) {
            console.error('Failed to check team access:', error);
            // On error, allow access (fail open for better UX)
        } finally {
            setLoading(false);
        }
    };

    // Show loading state while checking team access
    if (loading || authLoading) {
        return (
            <div className="flex items-center justify-center min-h-screen">
                <div className="flex items-center gap-3">
                    <Loader2 className="h-6 w-6 animate-spin" />
                    <span>Checking access...</span>
                </div>
            </div>
        );
    }

    // If user is part of a team but not the owner, show access denied
    if (userTeam && !isOwner) {
        return (
            <div className="container mx-auto max-w-2xl py-8">
                <Card className="p-8 text-center">
                    <div className="flex flex-col items-center gap-4">
                        <div className="p-3 bg-orange-100 dark:bg-orange-950/50 rounded-full">
                            <Users className="h-8 w-8 text-orange-600 dark:text-orange-400" />
                        </div>

                        <div className="space-y-2">
                            <h2 className="text-2xl font-semibold">Access Restricted</h2>
                            <p className="text-muted-foreground">
                                You're a member of "{userTeam.team.name}". Only team owners can access billing settings.
                            </p>
                        </div>

                        <div className="flex flex-col sm:flex-row gap-3 mt-6">
                            <Button
                                variant="outline"
                                onClick={() => window.history.back()}
                                className="flex items-center gap-2"
                            >
                                <ArrowLeft className="h-4 w-4" />
                                Go Back
                            </Button>
                            <Button
                                onClick={() => window.location.href = '/app'}
                                className="flex items-center gap-2"
                            >
                                Return to Dashboard
                            </Button>
                        </div>

                        <div className="mt-6 p-4 bg-muted rounded-lg text-sm text-muted-foreground">
                            <p>
                                If you need to manage your own billing, you can leave the team from your
                                <span className="font-medium"> Settings → Billing </span> page.
                            </p>
                        </div>
                    </div>
                </Card>
            </div>
        );
    }

    // Access is allowed (individual user or team owner)
    return <>{children}</>;
}
